"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import type { ComponentProps } from "react";

/**
 * `trimble-terra`'s `SiteNav` renders every header/mega-menu link (top-level
 * categories, subcategories, and nav promo cards) through this one
 * `LinkComponent`. It's used with `prefetch` left at Next's default (`true`
 * for links that scroll/stay into view), which is normally a nice
 * perf win — but it triggers a serious bug for any link into RC content
 * (`/blog/...`, `/resources/...` — anything matched by the `_bp-` rewrite in
 * `rcContentRewrites.ts`).
 *
 * Next.js 16's router sends prefetch requests with a `Rsc: 1` header plus a
 * `?_rsc=<hash>` cache-busting param. When the resolved route differs from
 * the requested pathname (true for any `rewrites()`-matched route), Next
 * responds with a 307 whose `Location` is meant to tell the router the
 * canonical URL to cache the prefetch under — but the value drops the
 * `_rsc` param's hash (`?_rsc` instead of `?_rsc=<hash>`), so the router
 * treats every reply as stale and retries immediately. Verified locally:
 * this hammers the same URL hundreds of times a second for the rest of the
 * page's lifetime, on every page (the header is global), the moment any RC
 * content link so much as flashes into view during initial layout/animation
 * — see https://github.com/vercel/next.js/issues/90658 for the same
 * `_rsc`-drop failure mode in the Next.js issue tracker.
 *
 * Disabling automatic (viewport/hover) prefetch for header links sidesteps
 * the bug entirely: a real click still navigates normally (that's a full
 * navigation, not a background prefetch, so it never enters the retry
 * loop), we just lose the "instant" feel of a pre-warmed cache for nav
 * links specifically.
 */
export default function NoPrefetchNavLink(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Link {...props} prefetch={false} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}